import {
  ENVIRONMENT_INITIALIZER,
  EnvironmentInjector,
  inject,
  makeEnvironmentProviders,
  runInInjectionContext,
} from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
} from '@angular/fire/compat/firestore';

/**
 * TEMPORARY workaround — remove when no longer needed.
 *
 * @angular/fire compat (19+) constructs AngularFirestoreDocument/Collection with
 * field-initializer `inject()`, which fails outside an injection context
 * (RxJS operators, async handlers, etc.). See angular/angularfire#3621.
 *
 * Remove this file and its provider in app.module.ts when either:
 * - we migrate off `@angular/fire/compat` to the modular API, or
 * - a stable @angular/fire release fixes compat so `.doc()`/`.collection()`
 *   work outside injection context without this patch.
 *
 * Wrap the factory methods so callers keep working until then.
 */
function wrapWithInjectionContext<T extends(...args: any[]) => any>(
  injector: EnvironmentInjector,
  fn: T,
): T {
  return <T> function(this: unknown, ...args: Parameters<T>) {
    return runInInjectionContext(injector, () => fn.apply(this, args));
  };
}

export function provideAngularFireCompatInjectionContextFix() {
  return makeEnvironmentProviders([
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        const injector = inject(EnvironmentInjector);

        const afsProto = <any> AngularFirestore.prototype;
        afsProto.doc = wrapWithInjectionContext(injector, afsProto.doc);
        afsProto.collection = wrapWithInjectionContext(injector, afsProto.collection);
        afsProto.collectionGroup = wrapWithInjectionContext(injector, afsProto.collectionGroup);

        const colProto = <any> AngularFirestoreCollection.prototype;
        colProto.doc = wrapWithInjectionContext(injector, colProto.doc);

        const docProto = <any> AngularFirestoreDocument.prototype;
        docProto.collection = wrapWithInjectionContext(injector, docProto.collection);
      },
    },
  ]);
}
